// src/components/Home.js
import React, { useState } from 'react';
import { Helmet } from "react-helmet";

import { Link } from 'react-router-dom';
import './Home.css';
import { useTranslation, Trans } from 'react-i18next';

// Section Components
import Tours from './tours';
import Testimonials from './testimonials';

const Home = () => {
  const { t } = useTranslation('home');
  const [showSpotlight, setShowSpotlight] = useState(false);

  return (
    
    <>
    <Helmet>
  {/* === BASIC META TAGS === */}
  <title>Olenini Safaris | Authentic Kenya Safari Tours & Responsible Travel</title>
  <meta
    name="description"
    content="Olenini Safaris is a trusted Kenya safari company offering authentic, tailor-made adventures across Maasai Mara, Amboseli, and Naivasha — with a commitment to community and conservation."
  />
  <meta
    name="keywords"
    content="Kenya safaris, Maasai Mara tours, Amboseli safaris, Kenya safari company, responsible tourism Kenya, tailor-made safaris, wildlife tours, ethical travel"
  />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://oleninisafaris.com/" />

  {/* === OPEN GRAPH (FACEBOOK / SOCIAL SHARING) === */}
  <meta property="og:title" content="Olenini Safaris | Authentic Kenya Safari Tours" />
  <meta
    property="og:description"
    content="Discover Kenya through responsible, tailor-made safaris with Olenini Safaris. Explore Maasai Mara, Amboseli, Naivasha, and more while supporting local communities."
  />
  <meta property="og:image" content="https://oleninisafaris.com/images/elephant.jpg" />
  <meta property="og:url" content="https://oleninisafaris.com/" />
  <meta property="og:type" content="website" />

  {/* === TWITTER META === */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Olenini Safaris | Kenya’s Trusted Safari Experts" />
  <meta
    name="twitter:description"
    content="Travel with purpose — Olenini Safaris crafts personalized Kenya safari adventures with heart, heritage, and conservation at the core."
  />
  <meta name="twitter:image" content="https://oleninisafaris.com/images/elephant.jpg" />

  {/* === ORGANIZATION STRUCTURED DATA === */}
  <script type="application/ld+json">{`
  {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    "name": "Olenini Tours & Safaris",
    "url": "https://oleninisafaris.com",
    "logo": "https://oleninisafaris.com/logo.png",
    "description": "Olenini Safaris offers personalized, responsible safaris in Kenya’s top destinations including Maasai Mara, Amboseli, Lake Nakuru, and Lake Naivasha. We combine adventure with community impact through our Osiligi Initiative.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Narok",
      "addressCountry": "KE"
    },
    "telephone": "+254702965315",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+254702965315",
      "contactType": "Customer Service",
      "availableLanguage": ["English", "Swahili"]
    },
    "founder": {
      "@type": "Person",
      "name": "Olenini Safaris Founder",
      "description": "Founder and passionate advocate for ethical safaris and community development in Kenya."
    },
   "sameAs": [
  "https://www.instagram.com/oleninisafaris/",
  "https://www.facebook.com/oleninisafaris/",
  "https://www.linkedin.com/company/olenini-safaris/"
],
    "areaServed": {
      "@type": "Country",
      "name": "Kenya"
    }
  }
  `}</script>

  {/* === SITELINKS STRUCTURED DATA (TRUST-FIRST PRIORITY PAGES) === */}
  <script type="application/ld+json">{`
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "SiteNavigationElement",
          "name": "Home",
          "url": "https://oleninisafaris.com/"
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "SiteNavigationElement",
          "name": "Safari Packages",
          "url": "https://oleninisafaris.com/tours"
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "SiteNavigationElement",
          "name": "Customize Your Safari",
          "url": "https://oleninisafaris.com/customizesafari"
        }
      },
      {
        "@type": "ListItem",
        "position": 4,
        "item": {
          "@type": "SiteNavigationElement",
          "name": "About Olenini",
          "url": "https://oleninisafaris.com/about"
        }
      },
      {
        "@type": "ListItem",
        "position": 5,
        "item": {
          "@type": "SiteNavigationElement",
          "name": "Osiligi Initiative",
          "url": "https://oleninisafaris.com/osiligi"
        }
      }
    ]
  }
  `}</script>
</Helmet>


      {/* HERO SECTION */}
      <div className="heroVideo-card">
  {!showSpotlight && (
    <button
      className="xmas-toggle-btn-centered"
      onClick={() => setShowSpotlight(true)}
    >
      <span role="img" aria-label="gift" className="xmas-emoji">🌴</span>
      {t('heroVideo.button')}
    </button>
  )}

  {showSpotlight && (
    <>
      <div
        className="spotlight-backdrop"
        onClick={() => setShowSpotlight(false)}
      />

      <div className="christmas-spotlight">
        <button
          onClick={() => setShowSpotlight(false)}
          className="close-spotlight"
        >
          ✖
        </button>

        <h3>{t('heroVideo.title')}</h3>

        <p>
          <Trans
            i18nKey="heroVideo.intro"
            ns="home"
            components={{ strong: <strong /> }}
          />
        </p>

        <ul className="christmas-benefits">
          <li>{t('heroVideo.list.upgrade')}</li>
          <li>{t('heroVideo.list.drives')}</li>
          <li>{t('heroVideo.list.bonfire')}</li>
          <li>{t('heroVideo.list.gifts')}</li>
        </ul>

        <Link to="/tours" className="christmas-btn">
          {t('heroVideo.cta')}
        </Link>

        <div className="christmas-footer-decoration">❄️</div>
      </div>
    </>
  )}

  <video
    src="/videos/PUNYUA.mp4"
    autoPlay
    loop
    muted
    playsInline
    aria-label="Safari scenes from Kenya"
  />
</div>


      {/* MISSION / IMPACT MESSAGE SECTION */}
      <div className="homeIntro-card">
        <p>
          <strong>{t('homeIntro.welcome')}</strong><br />
          <Trans 
            i18nKey="homeIntro.body"
            ns="home"
            components={{ strong: <strong />, br: <br /> }}
          />
        </p>
        <hr />
      </div>

      {/* DESTINATIONS */}
      <section id="tours" className="compact-section light-bg">
        <Tours />
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="compact-section alt-bg">
        <Testimonials />
      </section>
    </>
  );
};

export default Home;
